import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MdOutlineAddCard, MdOutlineAccountBalance } from "react-icons/md";
import { FaFileInvoiceDollar } from "react-icons/fa";

const BankLoanReimbursementNav = () => {
  const navigate = useNavigate();

  const cards = [
    {
      title: "Create Reimbursement",
      description: "Submit a new bank loan reimbursement request",
      icon: <MdOutlineAddCard className="text-white text-3xl" />,
      gradient: "from-green-500 to-emerald-600",
      onClick: () => navigate("/bank-loan-reimbursement/create"),
    },
    {
      title: "Select Bank Loan",
      description: "Choose an existing bank loan to process reimbursement",
      icon: <MdOutlineAccountBalance className="text-white text-3xl" />,
      gradient: "from-blue-500 to-indigo-600",
      onClick: () => navigate("/bank-loan-reimbursement/select-loan"),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8 font-montserrat">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-gradient-to-br from-green-200 to-green-500 p-2 rounded-xl">
              <FaFileInvoiceDollar className="text-white text-2xl" />
            </div>
            <h1 className="text-[25px] font-semibold text-slate-800">
              Bank Loan Reimbursement
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Manage and process employee bank loan reimbursements
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer"
              onClick={card.onClick}
            >
              {/* Gradient Overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
              ></div>

              {/* Animated Background Circles */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-200 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-200 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>

              {/* Content */}
              <div className="relative p-8 z-10">
                {/* Icon & Arrow Row */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                    <div
                      className={`relative bg-gradient-to-br ${card.gradient} p-4 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      {card.icon}
                    </div>
                  </div>

                  <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                    <svg
                      className="w-6 h-6 text-gray-400 group-hover:text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </div>
                </div>

                {/* Title & Description */}
                <h3 className="text-xl font-bold text-gray-800 group-hover:text-white transition-colors duration-300 mb-2">
                  {card.title}
                </h3>
                <p className="text-sm text-gray-600 group-hover:text-white/90 transition-colors duration-300 mb-6">
                  {card.description}
                </p>

                {/* Button */}
                <button className="w-full bg-blue-500 group-hover:bg-white/20 text-white rounded-xl px-6 py-3 transition-all duration-300 shadow-lg flex items-center justify-center gap-2">
                  <span>Proceed</span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default BankLoanReimbursementNav;